import { Controller } from "@hotwired/stimulus"
import SignaturePad from "signature_pad"

export default class extends Controller {
  static targets = [
    "submitBtn", "building", "roomNumbers", "departmentTitle", "formName", "projectNumber",
    "attachmentsList", "modalAttachmentsList",
    // Reminder modal
    "reminderModal",
    // Signature create modal
    "signatureModal", "sigTypeTab", "sigDrawTab", "sigTypeInput", "sigTypePreview", "sigCanvas",
    // Signature edit modal
    "signatureEditModal", "editSigTypeTab", "editSigDrawTab", "editSigTypeInput", "editSigTypePreview", "editSigCanvas",
    // Email submit modal
    "emailSubmitModal", "submitEmail0", "submitEmail1", "submitEmail2", "submitEmail3",
    "noDeanCheckbox", "deanEmailRow", "doAcademicScheduling",
    // Routing edit confirm modal
    "routingConfirmModal",
    // Clear-signature confirm modal (admin only)
    "clearConfirmModal",
    // Save draft modal
    "saveDraftModal", "draftEmail",
    // Validation modal
    "validationModal", "validationFields",
    // Attachments modal
    "attachmentsModal", "dropZone", "fileInput",
    // Project created modal
    "projectCreatedModal", "projectLoader", "projectReady", "projectLink"
  ]

  static values = {
    formType: String,       // "FMR" or "SRR"
    unitOptions: Array,
    formData: Object,
    token: String,
    attachments: Array,
    submitText: String,
    hasKParam: Boolean
  }

  connect() {
    this.signatures = {}
    this.attachmentIds = []
    this.activeSigField = null
    this.editingSigField = null
    this.signaturePad = null
    this.editSignaturePad = null

    // Load existing signature data
    this._loadExistingData()

    // Load existing attachments
    if (this.attachmentsValue && this.attachmentsValue.length > 0) {
      this.attachmentIds = this.attachmentsValue.map(a => String(a.id))
    }

    // Auto-height textareas
    this.element.querySelectorAll("textarea").forEach(ta => {
      ta.style.overflow = "hidden"
      this._adjustHeight(ta)
    })

    // Show reminder modal for saved forms (not in CREATE PROJECT mode)
    if (this.tokenValue && !this.hasKParamValue) {
      if (this.hasReminderModalTarget) this.reminderModalTarget.style.display = "block"
    }
  }

  // ─── Data Loading ──────────────────────────────────────────────

  _loadExistingData() {
    const d = this.formDataValue || {}
    const sigKeys = this.formTypeValue === "FMR"
      ? ["signature_1", "signature_2", "signature_3", "signature_4", "signature_5", "signature_6"]
      : ["signature_1", "signature_2", "signature_3", "signature_6"]

    sigKeys.forEach(key => {
      if (d[key] && d[key].date) {
        this.signatures[key] = { type: d[key].type, value: d[key].value, date: d[key].date }
      }
    })
  }

  // ─── Textarea Auto Height ──────────────────────────────────────

  autoHeight(event) {
    this._adjustHeight(event.target)
  }

  _adjustHeight(el) {
    el.style.height = "auto"
    el.style.height = el.scrollHeight + "px"
  }

  // ─── Signature Modal ──────────────────────────────────────────

  openSignature(event) {
    const sig = event.currentTarget.dataset.sig
    this.activeSigField = sig
    this.signatureModalTarget.style.display = "block"

    // Reset
    if (this.hasSigTypeInputTarget) this.sigTypeInputTarget.value = ""
    if (this.hasSigTypePreviewTarget) this.sigTypePreviewTarget.textContent = ""

    // Init SignaturePad
    requestAnimationFrame(() => {
      if (this.hasSigCanvasTarget) {
        const canvas = this.sigCanvasTarget
        canvas.width = canvas.offsetWidth
        canvas.height = canvas.offsetHeight
        this.signaturePad = new SignaturePad(canvas)
      }
    })

    // Show type tab by default
    this._showSigTab("type")
  }

  editSignature(event) {
    const sig = event.currentTarget.dataset.sig
    this.editingSigField = sig
    this.signatureEditModalTarget.style.display = "block"

    // Reset
    if (this.hasEditSigTypeInputTarget) this.editSigTypeInputTarget.value = ""
    if (this.hasEditSigTypePreviewTarget) this.editSigTypePreviewTarget.textContent = ""

    // Init edit SignaturePad
    requestAnimationFrame(() => {
      if (this.hasEditSigCanvasTarget) {
        const canvas = this.editSigCanvasTarget
        canvas.width = canvas.offsetWidth
        canvas.height = canvas.offsetHeight
        this.editSignaturePad = new SignaturePad(canvas)
      }
    })

    this._showEditSigTab("type")
  }

  switchSigTab(event) {
    const tab = event.currentTarget.dataset.tab
    this._showSigTab(tab)
  }

  _showSigTab(tab) {
    if (this.hasSigTypeTabTarget) this.sigTypeTabTarget.style.display = tab === "type" ? "block" : "none"
    if (this.hasSigDrawTabTarget) this.sigDrawTabTarget.style.display = tab === "draw" ? "block" : "none"

    this.element.querySelectorAll(".sig-tab").forEach(t => {
      t.style.borderBottomColor = t.dataset.tab === tab ? "var(--color-brand)" : "transparent"
      t.style.fontWeight = t.dataset.tab === tab ? "500" : "normal"
    })

    if (tab === "draw" && this.hasSigCanvasTarget) {
      requestAnimationFrame(() => {
        const canvas = this.sigCanvasTarget
        canvas.width = canvas.offsetWidth
        canvas.height = canvas.offsetHeight
        if (this.signaturePad) this.signaturePad.clear()
      })
    }
  }

  switchEditSigTab(event) {
    const tab = event.currentTarget.dataset.tab
    this._showEditSigTab(tab)
  }

  _showEditSigTab(tab) {
    if (this.hasEditSigTypeTabTarget) this.editSigTypeTabTarget.style.display = tab === "type" ? "block" : "none"
    if (this.hasEditSigDrawTabTarget) this.editSigDrawTabTarget.style.display = tab === "draw" ? "block" : "none"

    this.element.querySelectorAll(".edit-sig-tab").forEach(t => {
      t.style.borderBottomColor = t.dataset.tab === tab ? "var(--color-brand)" : "transparent"
      t.style.fontWeight = t.dataset.tab === tab ? "500" : "normal"
    })

    if (tab === "draw" && this.hasEditSigCanvasTarget) {
      requestAnimationFrame(() => {
        const canvas = this.editSigCanvasTarget
        canvas.width = canvas.offsetWidth
        canvas.height = canvas.offsetHeight
        if (this.editSignaturePad) this.editSignaturePad.clear()
      })
    }
  }

  updateSigPreview() {
    if (this.hasSigTypePreviewTarget) {
      this.sigTypePreviewTarget.textContent = this.sigTypeInputTarget.value
    }
  }

  updateEditSigPreview() {
    if (this.hasEditSigTypePreviewTarget) {
      this.editSigTypePreviewTarget.textContent = this.editSigTypeInputTarget.value
    }
  }

  clearSignature() {
    if (this.signaturePad) this.signaturePad.clear()
    if (this.hasSigTypeInputTarget) this.sigTypeInputTarget.value = ""
    if (this.hasSigTypePreviewTarget) this.sigTypePreviewTarget.textContent = ""
  }

  clearEditSignature() {
    if (this.editSignaturePad) this.editSignaturePad.clear()
    if (this.hasEditSigTypeInputTarget) this.editSigTypeInputTarget.value = ""
    if (this.hasEditSigTypePreviewTarget) this.editSigTypePreviewTarget.textContent = ""
  }

  createSignature() {
    const sig = this.activeSigField
    if (!sig) return

    const now = new Date()
    const dateStr = now.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) + " " + now.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })

    // Check draw tab first
    const isDrawVisible = this.hasSigDrawTabTarget && this.sigDrawTabTarget.style.display !== "none"
    if (isDrawVisible && this.signaturePad && !this.signaturePad.isEmpty()) {
      this.signatures[sig] = { type: "draw", value: this.signaturePad.toDataURL(), date: dateStr }
    } else if (this.hasSigTypeInputTarget && this.sigTypeInputTarget.value.trim()) {
      this.signatures[sig] = { type: "text", value: this.sigTypeInputTarget.value.trim(), date: dateStr }
    } else {
      return // nothing to create
    }

    this._renderSignatureBox(sig)
    this.signatureModalTarget.style.display = "none"
  }

  updateSignature() {
    const sig = this.editingSigField
    if (!sig) return

    const now = new Date()
    const dateStr = now.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) + " " + now.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })

    const isDrawVisible = this.hasEditSigDrawTabTarget && this.editSigDrawTabTarget.style.display !== "none"
    if (isDrawVisible && this.editSignaturePad && !this.editSignaturePad.isEmpty()) {
      this.signatures[sig] = { type: "draw", value: this.editSignaturePad.toDataURL(), date: dateStr }
    } else if (this.hasEditSigTypeInputTarget && this.editSigTypeInputTarget.value.trim()) {
      this.signatures[sig] = { type: "text", value: this.editSigTypeInputTarget.value.trim(), date: dateStr }
    } else {
      return
    }

    this._renderSignatureBox(sig)
    this.signatureEditModalTarget.style.display = "none"
  }

  removeSignature() {
    const sig = this.editingSigField
    if (!sig) return

    delete this.signatures[sig]
    this._renderSignatureBox(sig)
    this.signatureEditModalTarget.style.display = "none"
  }

  _renderSignatureBox(sig) {
    // Map sig name to signature key number
    const sigMap = { sig1: "signature_1", sig2: "signature_2", sig3: "signature_3", sig4: "signature_4", sig5: "signature_5", sig6: "signature_6" }
    const sigKey = sigMap[sig] || sig
    const sigData = this.signatures[sigKey] || this.signatures[sig]
    const box = this.element.querySelector(`.sig-box[data-sig="${sig}"]`)
    if (!box) return

    // Keep the label
    const label = box.querySelector("span[style*='position: absolute']")
    box.innerHTML = ""
    if (label) box.appendChild(label)

    if (sigData && sigData.date) {
      const wrapper = document.createElement("div")
      wrapper.setAttribute("data-action", "click->uvu-form#editSignature")
      wrapper.setAttribute("data-sig", sig)
      wrapper.style.cssText = "width: 100%; display: flex; justify-content: center; align-items: center; cursor: pointer"

      if (sigData.type === "text") {
        const span = document.createElement("span")
        span.setAttribute("data-value", sigData.value)
        span.className = "sig text"
        span.style.cssText = "font-family: 'Allura' !important; font-size: 45px;"
        span.textContent = sigData.value
        wrapper.appendChild(span)
      } else if (sigData.type === "draw") {
        const img = document.createElement("img")
        img.setAttribute("data-value", sigData.value)
        img.className = "sig draw"
        img.style.cssText = "height: 100%"
        img.src = sigData.value
        wrapper.appendChild(img)
      }

      const dateSpan = document.createElement("span")
      dateSpan.className = "sig-date"
      dateSpan.style.cssText = "position: absolute; right: 5px; bottom: 2px; font-size: 10px; color: var(--color-brand);"
      dateSpan.textContent = sigData.date
      wrapper.appendChild(dateSpan)

      box.appendChild(wrapper)
    } else {
      const btn = document.createElement("span")
      btn.setAttribute("data-action", "click->uvu-form#openSignature")
      btn.setAttribute("data-sig", sig)
      btn.style.cssText = "padding: 3px 5px; background-color: var(--color-gray-400); color: white; cursor: pointer; height: 26px; border-radius: 5px; margin: auto; width: 100px; text-align: center"
      btn.textContent = "SIGN HERE"
      box.appendChild(btn)
    }
  }

  // ─── Modal Helpers ─────────────────────────────────────────────

  closeModal() {
    if (this.hasReminderModalTarget) this.reminderModalTarget.style.display = "none"
    if (this.hasSignatureModalTarget) this.signatureModalTarget.style.display = "none"
    if (this.hasSignatureEditModalTarget) this.signatureEditModalTarget.style.display = "none"
    if (this.hasEmailSubmitModalTarget) this.emailSubmitModalTarget.style.display = "none"
    if (this.hasSaveDraftModalTarget) this.saveDraftModalTarget.style.display = "none"
    if (this.hasValidationModalTarget) this.validationModalTarget.style.display = "none"
    if (this.hasRoutingConfirmModalTarget) this.routingConfirmModalTarget.style.display = "none"
    if (this.hasClearConfirmModalTarget) this.clearConfirmModalTarget.style.display = "none"
    if (this.hasAttachmentsModalTarget) { this.attachmentsModalTarget.classList.add("hidden"); this.attachmentsModalTarget.classList.remove("flex") }
    if (this.hasProjectCreatedModalTarget) this.projectCreatedModalTarget.style.display = "none"
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  // ─── Dean Toggle ───────────────────────────────────────────────

  toggleDean() {
    const checked = this.noDeanCheckboxTarget.checked
    if (this.hasDeanEmailRowTarget) {
      this.deanEmailRowTarget.style.display = checked ? "none" : "block"
    }
  }

  // ─── Validation ────────────────────────────────────────────────

  _validateForm(strict = true) {
    const missing = []
    const building = this.hasBuildingTarget ? this.buildingTarget.value : ""
    const roomNumbers = this.hasRoomNumbersTarget ? this.roomNumbersTarget.value : ""
    const deptTitle = this.hasDepartmentTitleTarget ? this.departmentTitleTarget.value : ""
    const name = this.hasFormNameTarget ? this.formNameTarget.value : ""

    if (!building) missing.push("Building Name or Location")
    if (!deptTitle) missing.push("Department/Title")
    if (!roomNumbers) missing.push("Room Number(s)")
    if (!name) missing.push("Name")

    if (strict) {
      const q1 = document.getElementById("first_question")
      const q2 = document.getElementById("second_question")
      if (q1 && !q1.value) missing.push("First Question")
      if (q2 && !q2.value) missing.push("Second Question")

      // When academic scheduling is included, the manager's name is required.
      const emailsEl = document.getElementById("emails")
      if (emailsEl && emailsEl.dataset.doAcademicScheduling === "yes") {
        const acadName = document.getElementById("name_of_academic_scheduler")
        if (acadName && !acadName.value.trim()) missing.push("Manager, Academic Scheduling Office")
      }

      if (this.formTypeValue === "FMR") {
        const q3 = document.getElementById("third_question")
        if (q3 && !q3.value) missing.push("Third Question")

        const justification = document.getElementById("project_justification")
        if (justification && !justification.value) missing.push("Project Justification")

        const description = document.getElementById("project_description")
        if (description && !description.value) missing.push("Project Description")

        const indexCode = document.getElementById("index_code")
        if (indexCode && !indexCode.value) missing.push("Index Code #")
      } else {
        const justification = document.getElementById("project_justification")
        if (justification && !justification.value) missing.push("Justification/Explanation")
      }
    }

    return missing
  }

  // ─── Collect Form Data ─────────────────────────────────────────

  _collectData() {
    const emailsEl = document.getElementById("emails")
    const d = {}

    d.type = this.formTypeValue
    d.building_name_or_location = this.hasBuildingTarget ? this.buildingTarget.value : ""
    d.room_numbers = this.hasRoomNumbersTarget ? this.roomNumbersTarget.value : ""
    d.department_title = this.hasDepartmentTitleTarget ? this.departmentTitleTarget.value : ""
    d.name = this.hasFormNameTarget ? this.formNameTarget.value : ""

    d.name_of_department_head = (document.getElementById("name_of_department_head") || {}).value || ""
    d.name_of_dean = (document.getElementById("name_of_dean") || {}).value || ""
    d.name_of_vp = (document.getElementById("name_of_vp") || {}).value || ""
    d.name_of_academic_scheduler = (document.getElementById("name_of_academic_scheduler") || {}).value || ""

    d.first_question = (document.getElementById("first_question") || {}).value || ""
    d.second_question = (document.getElementById("second_question") || {}).value || ""
    d.project_justification = (document.getElementById("project_justification") || {}).value || ""
    d.project_additional_comments = (document.getElementById("project_additional_comments") || {}).value || ""

    if (this.formTypeValue === "FMR") {
      d.third_question = (document.getElementById("third_question") || {}).value || ""
      d.project_description = (document.getElementById("project_description") || {}).value || ""
      d.index_code = (document.getElementById("index_code") || {}).value || ""
      d.authorized_signature_name = (document.getElementById("authorized_signature_name") || {}).value || ""
      d.name_of_pm = (document.getElementById("name_of_pm") || {}).value || ""
      d.pm_comments = (document.getElementById("pm_comments") || {}).value || ""
      d.project_number = this.hasProjectNumberTarget ? this.projectNumberTarget.value : ""
    }

    // Signatures
    d.signature_1 = this.signatures.signature_1 || this.signatures.sig1 || { type: "", value: "", date: "" }
    d.signature_2 = this.signatures.signature_2 || this.signatures.sig2 || { type: "", value: "", date: "" }
    d.signature_3 = this.signatures.signature_3 || this.signatures.sig3 || { type: "", value: "", date: "" }
    d.signature_6 = this.signatures.signature_6 || this.signatures.sig6 || { type: "", value: "", date: "" }

    if (this.formTypeValue === "FMR") {
      d.signature_4 = this.signatures.signature_4 || this.signatures.sig4 || { type: "", value: "", date: "" }
      d.signature_5 = this.signatures.signature_5 || this.signatures.sig5 || { type: "", value: "", date: "" }
    }

    // Emails
    if (emailsEl) {
      d.email0 = emailsEl.dataset.email0 || ""
      d.email1 = emailsEl.dataset.email1 || ""
      d.email2 = emailsEl.dataset.email2 || ""
      d.email3 = emailsEl.dataset.email3 || ""
      d.do_academic_scheduling = emailsEl.dataset.doAcademicScheduling || ""
      d.no_dean = emailsEl.dataset.noDean === "true"
    }

    return d
  }

  // ─── Determine Next Signer ─────────────────────────────────────

  _determineNextSigner(d) {
    // Check signature completion to determine next signer
    const sig1Done = d.signature_1 && d.signature_1.date
    const sig2Done = d.signature_2 && d.signature_2.date
    const sig3Done = d.signature_3 && d.signature_3.date
    const sig6Done = d.signature_6 && d.signature_6.date
    const noDean = d.no_dean
    const doAcadScheduling = d.do_academic_scheduling === "yes"

    if (!sig1Done) return "dept_head"
    if (!sig2Done && !noDean) return "dean"
    if (!sig6Done && doAcadScheduling) return "academic_scheduler"

    if (this.formTypeValue === "FMR") {
      const sig5Done = d.signature_5 && d.signature_5.date
      if (!sig5Done) return "director"
    }

    if (!sig3Done) return "vp"

    return "activation_email"
  }

  // ─── Routing Edit (re-open a skipped signer) ───────────────────
  // For forms submitted with academic scheduling mistakenly set to "no".
  // Only rendered for authorized viewers (server-side @can_edit_routing).

  enableAcademicScheduling(event) {
    if (event) event.preventDefault()
    if (this.hasRoutingConfirmModalTarget) this.routingConfirmModalTarget.style.display = "block"
  }

  confirmEnableAcademicScheduling() {
    if (this.hasRoutingConfirmModalTarget) this.routingConfirmModalTarget.style.display = "none"

    // Swap the static "N/A" labels for the editable name input + signature box.
    document.querySelectorAll(".js-no-acad-label").forEach(el => { el.style.display = "none" })
    document.querySelectorAll(".js-acad-row").forEach(el => { el.style.display = "flex" })

    // Persist the routing decision so _collectData / _determineNextSigner pick it up.
    const emailsEl = document.getElementById("emails")
    if (emailsEl) emailsEl.dataset.doAcademicScheduling = "yes"
    this.routingReopened = true

    const acadName = document.getElementById("name_of_academic_scheduler")
    if (acadName) acadName.focus()
  }

  // ─── Clear Signature (admin: clear a wrongly-signed line & reopen) ───
  // The "Clear" control is only rendered server-side for UVU admins
  // (@is_uvu_admin); the endpoint re-checks the same authorization.

  clearSignaturePrompt(event) {
    if (event) { event.preventDefault(); event.stopPropagation() }
    this.pendingClearSigKey = event.currentTarget.dataset.sigKey
    if (this.hasClearConfirmModalTarget) this.clearConfirmModalTarget.style.display = "block"
  }

  confirmClearSignature() {
    const sigKey = this.pendingClearSigKey
    if (!sigKey || !this.tokenValue) return
    if (this.hasClearConfirmModalTarget) this.clearConfirmModalTarget.style.display = "none"

    const formData = new FormData()
    formData.append("token", this.tokenValue)
    formData.append("signature_key", sigKey)

    fetch("/design/reset_custom_form_signature", {
      method: "POST",
      body: formData,
      headers: { "X-CSRF-Token": this._csrfToken() }
    }).then(response => {
      if (!response.ok) throw new Error(`Request failed (${response.status})`)
      window.location.reload()
    }).catch(() => this._showToast("Could not clear the signature. Please try again.", "alert"))
  }

  // ─── Submit Form ───────────────────────────────────────────────

  submitForm() {
    // If CREATE PROJECT mode
    if (this.hasKParamValue) {
      this._createProject()
      return
    }

    const missing = this._validateForm(true)
    if (missing.length > 0) {
      this._showValidationModal(missing)
      return
    }

    const d = this._collectData()

    // Check if emails are already set
    const emailsEl = document.getElementById("emails")
    const hasEmails = emailsEl &&
      emailsEl.dataset.email1 &&
      (emailsEl.dataset.email2 || emailsEl.dataset.noDean === "true") &&
      emailsEl.dataset.email3

    if (hasEmails) {
      this._sendToServer(d)
    } else {
      // Show email collection modal
      this.emailSubmitModalTarget.style.display = "block"
    }
  }

  confirmSubmit() {
    // Validate emails
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    const email0 = this.submitEmail0Target.value
    const email1 = this.submitEmail1Target.value
    const noDean = this.noDeanCheckboxTarget.checked
    const email2 = noDean ? "" : this.submitEmail2Target.value
    const email3 = this.submitEmail3Target.value
    const doAcadScheduling = this.doAcademicSchedulingTarget.value

    const missing = []
    if (!emailRegex.test(email0)) missing.push("Form Creator Email")
    if (!emailRegex.test(email1)) missing.push("Department Head Email")
    if (!noDean && !emailRegex.test(email2)) missing.push("Dean Email")
    if (!email3) missing.push("Vice President")
    if (!doAcadScheduling) missing.push("Academic Scheduling")
    if (missing.length > 0) { this._showValidationModal(missing); return }

    // Store emails in hidden element
    const emailsEl = document.getElementById("emails")
    emailsEl.dataset.email0 = email0
    emailsEl.dataset.email1 = email1
    emailsEl.dataset.email2 = email2
    emailsEl.dataset.email3 = email3
    emailsEl.dataset.noDean = noDean
    emailsEl.dataset.doAcademicScheduling = doAcadScheduling

    this.emailSubmitModalTarget.style.display = "none"

    const d = this._collectData()
    d.email0 = email0
    d.email1 = email1
    d.email2 = email2
    d.email3 = email3
    d.no_dean = noDean
    d.do_academic_scheduling = doAcadScheduling

    this._sendToServer(d)
  }

  _sendToServer(d) {
    const nextSigner = this._determineNextSigner(d)

    const formData = new FormData()
    formData.append("data", JSON.stringify(d))
    formData.append("client", "uvu")
    formData.append("next_signer", nextSigner)
    formData.append("attachment_ids", this.attachmentIds.join(" "))
    if (this.routingReopened) formData.append("routing_reopened", "1")

    if (this.tokenValue) {
      formData.append("token", this.tokenValue)
    }

    fetch("/submit_custom_form", {
      method: "POST",
      body: formData,
      headers: { "X-CSRF-Token": this._csrfToken() }
    })
    .then(response => {
      if (response.ok) {
        window.location.href = "/pdf_submitted_successfully"
      } else {
        this._showToast("An error occurred while submitting the form.", "alert")
      }
    })
    .catch(() => this._showToast("An error occurred while submitting the form.", "alert"))
  }

  // ─── Save Draft ────────────────────────────────────────────────

  saveDraft() {
    const missing = this._validateForm(false)
    if (missing.length > 0) {
      this._showValidationModal(missing)
      return
    }

    this.saveDraftModalTarget.style.display = "block"
  }

  confirmSaveDraft() {
    const email = this.draftEmailTarget.value
    // No double dots or a dot right after "@" — "birkam@uvu..edu" passed the
    // old check, the email never went out, and the toast still said it had.
    if (!email || !/^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/.test(email.trim())) {
      this._showValidationModal(["Email"])
      return
    }

    this.saveDraftModalTarget.style.display = "none"

    const d = this._collectData()
    const formData = new FormData()
    formData.append("data", JSON.stringify(d))
    formData.append("client", "uvu")
    formData.append("email", email)
    formData.append("attachment_ids", this.attachmentIds.join(" "))

    if (this.tokenValue) {
      formData.append("token", this.tokenValue)
    }

    fetch("/save_custom_form_data", {
      method: "POST",
      body: formData,
      headers: { "X-CSRF-Token": this._csrfToken() }
    })
    .then(response => response.json())
    .then(data => {
      if (data.token) {
        this.tokenValue = data.token
        this._showToast("Draft saved successfully! An email with the draft link has been sent.", "notice")
      }
    })
    .catch(() => this._showToast("An error occurred while saving the draft.", "alert"))
  }

  // ─── Create Project ────────────────────────────────────────────

  _createProject() {
    const d = this._collectData()

    this.projectCreatedModalTarget.style.display = "block"
    if (this.hasProjectLoaderTarget) this.projectLoaderTarget.style.display = "block"
    if (this.hasProjectReadyTarget) this.projectReadyTarget.style.display = "none"

    const formData = new FormData()
    formData.append("data", JSON.stringify(d))
    formData.append("token", this.tokenValue)
    formData.append("attachment_ids", this.attachmentIds.join(" "))

    fetch("/turn_custom_form_into_dev_project", {
      method: "POST",
      body: formData,
      headers: { "X-CSRF-Token": this._csrfToken() }
    })
    .then(response => response.json())
    .then(data => {
      if (this.hasProjectLoaderTarget) this.projectLoaderTarget.style.display = "none"
      if (this.hasProjectReadyTarget) this.projectReadyTarget.style.display = "block"
      if (this.hasProjectLinkTarget && data.project_url) {
        this.projectLinkTarget.innerHTML = `<a href="${data.project_url}" target="_blank" style="color: var(--color-brand);">${data.project_url}</a>`
      }
    })
    .catch(() => {
      if (this.hasProjectLoaderTarget) this.projectLoaderTarget.textContent = "An error occurred."
    })
  }

  // ─── Validation Modal ──────────────────────────────────────────

  _showValidationModal(missing) {
    if (this.hasValidationFieldsTarget) {
      this.validationFieldsTarget.innerHTML = missing.map(f => `<span>• ${f}</span>`).join("")
    }
    this.validationModalTarget.style.display = "block"
  }

  // ─── Attachments ───────────────────────────────────────────────

  openAttachments() {
    this.attachmentsModalTarget.classList.remove("hidden")
    this.attachmentsModalTarget.classList.add("flex")
    this._refreshModalAttachments()
  }

  triggerFileInput() {
    if (this.hasFileInputTarget) this.fileInputTarget.click()
  }

  dragOver(event) {
    event.preventDefault()
    this.dropZoneTarget.classList.add("border-brand", "bg-brand/5")
  }

  dragLeave(event) {
    event.preventDefault()
    this.dropZoneTarget.classList.remove("border-brand", "bg-brand/5")
  }

  dropFiles(event) {
    event.preventDefault()
    this.dropZoneTarget.classList.remove("border-brand", "bg-brand/5")
    const files = event.dataTransfer.files
    this._uploadFiles(files)
  }

  selectFiles(event) {
    const files = event.target.files
    this._uploadFiles(files)
    event.target.value = ""
  }

  // Browsers do not reliably report "application/pdf" — Windows without a registered
  // PDF handler, network shares and some mail clients send "application/octet-stream"
  // or an empty type, so check the file name too before rejecting an upload.
  _isPdf(file) {
    return (file.type || "").toLowerCase().includes("pdf") || /\.pdf$/i.test(file.name || "")
  }

  _uploadFiles(files) {
    for (const file of files) {
      if (!this._isPdf(file)) {
        this._showUploadError(`${file.name || "That file"} is not a PDF — only PDF files can be attached.`)
        continue
      }

      // Create uploading chip (blue with spinner)
      const chip = this._createFileChip(file.name, { uploading: true })
      this.modalAttachmentsListTarget.appendChild(chip)

      const uploadData = new FormData()
      uploadData.append("photo[files][]", file)

      fetch("/design/uvu_upload_files", {
        method: "POST",
        body: uploadData,
        headers: { "X-CSRF-Token": this._csrfToken() }
      })
      .then(response => {
        if (response.status === 413) throw new Error("too_large")
        if (!response.ok) throw new Error(`http_${response.status}`)
        return response.json()
      })
      .then(data => {
        chip.remove()
        const documents = data.documents || []
        this._uploadedFiles = this._uploadedFiles || {}
        documents.forEach(doc => {
          this.attachmentIds.push(String(doc.id))
          this._uploadedFiles[String(doc.id)] = doc
          const doneChip = this._createFileChip(doc.filename || file.name || "File", { removable: true, attachmentId: doc.id, fileUrl: doc.file_url })
          this.modalAttachmentsListTarget.appendChild(doneChip)
          this._syncSidebarAttachments()
        })
        // Never let a file disappear without saying why: the server reports what it
        // refused, and an empty response with no reason still gets a message.
        ;(data.errors || []).forEach(message => this._showUploadError(message))
        if (documents.length === 0 && (data.errors || []).length === 0) {
          this._showUploadError(`${file.name || "That file"} could not be attached. Please try again.`)
        }
      })
      .catch(error => {
        chip.remove()
        if (error && error.message === "too_large") {
          this._showUploadError(`${file.name || "That file"} is too large to upload. Please attach a smaller PDF.`)
        } else {
          this._showUploadError(`${file.name || "That file"} failed to upload. Please try again.`)
        }
      })
    }
  }

  _createFileChip(name, opts = {}) {
    const truncated = name.length > 28 ? name.slice(0, 24) + "..." + name.slice(name.lastIndexOf(".")) : name
    const chip = document.createElement("div")
    chip.className = "inline-flex items-center gap-1.5 pl-2 pr-2 py-1 rounded-md text-xs"

    if (opts.uploading) {
      chip.classList.add("chip-uploading")
      chip.innerHTML = `
        <svg class="w-3.5 h-3.5 shrink-0 animate-spin" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
        </svg>
        <span class="truncate max-w-[180px]" title="${name}">${truncated}</span>
      `
    } else {
      chip.classList.add("chip-file")
      if (opts.attachmentId) chip.dataset.attachmentId = opts.attachmentId
      chip.innerHTML = `
        <svg class="w-3.5 h-3.5 shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z"/>
        </svg>
        ${opts.fileUrl ? `<a href="${opts.fileUrl}" target="_blank" class="truncate max-w-[180px] hover:underline" title="${name}">${truncated}</a>` : `<span class="truncate max-w-[180px]" title="${name}">${truncated}</span>`}
        ${opts.removable ? `<button type="button" class="ml-0.5 p-0.5 rounded hover:bg-gray-300 text-gray-400 hover:text-gray-600 cursor-pointer" data-action="click->uvu-form#removeAttachment" data-attachment-id="${opts.attachmentId}">
          <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
        </button>` : ""}
      `
    }
    return chip
  }

  _showUploadError(msg) {
    const el = document.createElement("div")
    el.className = "w-full text-[13px] text-red-500 mt-1"
    el.textContent = msg
    this.modalAttachmentsListTarget.appendChild(el)
    setTimeout(() => el.remove(), 8000)
  }

  _syncSidebarAttachments() {
    if (!this.hasAttachmentsListTarget) return
    this.attachmentsListTarget.innerHTML = ""
    // Only select top-level chip divs, not nested buttons
    this.modalAttachmentsListTarget.querySelectorAll(":scope > [data-attachment-id]").forEach(chip => {
      const id = chip.dataset.attachmentId
      const link = chip.querySelector("a")
      const name = link?.textContent || chip.querySelector(".truncate")?.textContent || "File"
      const url = link?.href || "#"
      const row = document.createElement("div")
      row.className = "flex items-center justify-between py-1 border-b border-gray-100 text-[13px]"
      row.dataset.attachmentId = id
      row.innerHTML = `
        <a href="${url}" target="_blank" class="text-brand hover:underline truncate flex-1">${name}</a>
        <button data-action="click->uvu-form#removeAttachmentSidebar" data-attachment-id="${id}" class="text-red-400 hover:text-red-600 cursor-pointer ml-2">
          <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
        </button>
      `
      this.attachmentsListTarget.appendChild(row)
    })
  }

  _refreshModalAttachments() {
    if (!this.hasModalAttachmentsListTarget) return
    this.modalAttachmentsListTarget.innerHTML = ""

    // Build a lookup of known files from initial server data
    const knownFiles = {}
    if (this.attachmentsValue && this.attachmentsValue.length > 0) {
      this.attachmentsValue.forEach(att => { knownFiles[String(att.id)] = att })
    }
    // Also include dynamically uploaded files
    this._uploadedFiles = this._uploadedFiles || {}

    // Rebuild chips for all current attachment IDs
    this.attachmentIds.forEach(id => {
      const att = knownFiles[id] || this._uploadedFiles[id]
      if (!att) return
      const chip = this._createFileChip(att.filename || att.new_name || "File", { removable: true, attachmentId: att.id, fileUrl: att.file_url })
      this.modalAttachmentsListTarget.appendChild(chip)
    })
  }

  removeAttachment(event) {
    event.stopPropagation()
    const id = event.currentTarget.dataset.attachmentId
    this.attachmentIds = this.attachmentIds.filter(a => a !== String(id))
    // Remove chip from modal
    this.modalAttachmentsListTarget.querySelectorAll(`[data-attachment-id="${id}"]`).forEach(el => el.remove())
    this._syncSidebarAttachments()
  }

  removeAttachmentSidebar(event) {
    const id = event.currentTarget.dataset.attachmentId
    this.attachmentIds = this.attachmentIds.filter(a => a !== String(id))
    // Remove from sidebar
    event.currentTarget.closest("[data-attachment-id]")?.remove()
    // Remove from modal
    this.modalAttachmentsListTarget.querySelectorAll(`[data-attachment-id="${id}"]`).forEach(el => el.remove())
  }

  // ─── Helpers ───────────────────────────────────────────────────

  _showToast(message, type = "notice") {
    const cssClass = type === "notice" ? "flash-toast-notice" : "flash-toast-alert"
    const toast = document.createElement("div")
    toast.className = cssClass
    toast.setAttribute("data-controller", "flash")
    toast.setAttribute("data-flash-dismiss-after-value", "5000")
    toast.innerHTML = `
      <div class="flex items-center justify-between gap-3">
        <span>${message}</span>
        <button data-action="click->flash#dismiss" class="text-current opacity-60 hover:opacity-100 cursor-pointer">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
        </button>
      </div>
    `
    document.body.prepend(toast)
  }

  _csrfToken() {
    const meta = document.querySelector("meta[name='csrf-token']")
    return meta ? meta.content : ""
  }
}
